import React from 'react';
import { z } from 'zod';
import { Nullable } from 'global';
import { useQuery } from '@apollo/client';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField, AutocompleteField, SelectField } from 'components/form';
import { Paper, Avatar, Typography, Box, Button, Stack } from '@mui/material';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { dealActivityApi } from '../../api';
import { calendarApi } from '../../calendar/api';
import { callDirectionOptions, callStatusOptions } from '../../constants';

const callFormSchema = z.object({
  status: z.string(),
  direction: z.string(),
  subject: z.string().min(1).max(300),
  description: z.string().min(1).max(1000),
  contact: z.object({ label: z.string(), value: z.string().uuid() }).nullable(),
});

type CallActivityFormValues = z.infer<typeof callFormSchema>;

interface Props {
  isEdit: boolean;
  onCancel: VoidFunction;
  isSubmitting: boolean;
  initialValues: Nullable<CallActivityFormValues>;
  onSubmit: (formValues: CallActivityFormValues) => Promise<void>;
  accountId?: Nullable<string>;
}

export const CallActivityForm = ({ isEdit, onSubmit, onCancel, isSubmitting, initialValues, accountId }: Props) => {
  const { data: usersData, loading: usersLoading } = useQuery(dealActivityApi.dealContacts, { skip: !!accountId });

  // Calls logged against an account pick from that account's contacts; network-only so contacts added
  // on the account page show up without a reload
  const { data: accountContactsData, loading: accountContactsLoading } = useQuery(
    calendarApi.events.queries.contactOptions,
    {
      skip: !accountId,
      fetchPolicy: 'network-only',
      variables: { input: { accountId } },
    }
  );

  const contactOptions = accountId
    ? (accountContactsData?.dealActivity?.result?.list ?? [])
    : (usersData?.dealActivity?.dealContacts ?? []);

  const loading = usersLoading || accountContactsLoading;

  const form = useForm<CallActivityFormValues>({
    resolver: zodResolver(callFormSchema),
    defaultValues: initialValues ?? {
      subject: '',
      contact: null,
      description: '',
      status: 'ANSWERED',
      direction: 'INCOMING',
    },
  });

  const contact = useWatch({ control: form.control, name: 'contact' });

  return (
    <Stack gap="1rem">
      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
        <Stack gap="1rem">
          <AutocompleteField
            size="small"
            name="contact"
            disabled={isEdit}
            loading={loading}
            control={form.control}
            options={contactOptions}
            textFieldProps={{
              required: true,
              variant: 'outlined',
              label: 'Contact Search',
            }}
          />

          <SelectField
            required
            name="direction"
            variant="outlined"
            label="Call Direction"
            control={form.control}
            options={callDirectionOptions}
          />

          <SelectField
            required
            name="status"
            variant="outlined"
            label="Call Status"
            control={form.control}
            options={callStatusOptions}
          />

          <InputField control={form.control} name="subject" label="Call Subject" inputProps={{ variant: 'outlined' }} />

          <InputField
            name="description"
            control={form.control}
            inputProps={{
              minRows: 5,
              multiline: true,
              variant: 'outlined',
              placeholder: 'Description',
            }}
          />
        </Stack>

        <Paper sx={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Stack direction="row" alignItems="center" gap="1rem">
            <Avatar />

            <RenderWithCondition
              condition={!!contact}
              fallBack={
                <Typography variant="subtitle1" component="span" fontWeight="500">
                  No contact selected
                </Typography>
              }
            >
              <Typography>{contact?.label}</Typography>
            </RenderWithCondition>
          </Stack>

          {!contact && <Typography color="textSecondary">Start by choosing a contact to view their details</Typography>}
        </Paper>
      </Box>

      <Stack direction="row" gap="1rem">
        <Button loading={isSubmitting} variant="contained" onClick={form.handleSubmit(onSubmit)}>
          {isEdit ? 'Edit call' : 'Add call'}
        </Button>

        <Button disabled={isSubmitting} onClick={onCancel}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};
